import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { portalApi, type PortalStaffCourseStatusDto } from '@/api/portal'
import { CertificateViewerModal } from '@/components/shared/CertificateViewerModal'
import {
  CheckCircle2, AlertTriangle, Clock, MinusCircle, ChevronRight,
  Search, X, Eye, Download, CheckSquare, Square, ListChecks,
} from 'lucide-react'
import { usePortalDates } from '@/hooks/useDates'

type Status = PortalStaffCourseStatusDto['status']

function StatusBadge({ status, expiryDate }: { status: Status; expiryDate: string | null }) {
  const { formatDate } = usePortalDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }
  if (status === 'Valid')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
        <CheckCircle2 size={13} />
        {expiryDate ? fmtDate(expiryDate) : 'Valid'}
      </span>
    )
  if (status === 'NoExpiry')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
        <CheckCircle2 size={13} /> No expiry
      </span>
    )
  if (status === 'DueSoon')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
        <Clock size={13} />
        {expiryDate ? fmtDate(expiryDate) : 'Due soon'}
      </span>
    )
  if (status === 'Overdue')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
        <AlertTriangle size={13} />
        {expiryDate ? fmtDate(expiryDate) : 'Overdue'}
      </span>
    )
  // NotCertified
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
      <MinusCircle size={13} /> Not certified
    </span>
  )
}

function StatusCell({ status }: { status: Status }) {
  const bg: Record<Status, string> = {
    Valid: 'bg-green-50',
    NoExpiry: 'bg-green-50',
    DueSoon: 'bg-amber-50',
    Overdue: 'bg-red-50',
    NotCertified: 'bg-gray-50',
  }
  return bg[status]
}

export default function CompliancePage() {
  const navigate = useNavigate()
  const [selectedCourse, setSelectedCourse] = useState<string | 'all'>('all')
  const [search, setSearch] = useState('')
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [downloading, setDownloading] = useState(false)
  const [viewerUrl, setViewerUrl] = useState<string | null>(null)
  const [viewerContentType, setViewerContentType] = useState('application/pdf')
  const [viewerTitle, setViewerTitle] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['portal-compliance'],
    queryFn: () => portalApi.getCompliance().then(r => r.data),
  })

  if (isLoading) return <p className="text-gray-500">Loading…</p>
  if (!data) return null

  const { courses, staff } = data

  if (courses.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Compliance</h1>
        <p className="text-gray-500">No training records found for your team yet.</p>
      </div>
    )
  }

  // Filter: which courses to show in the grid
  const visibleCourses = selectedCourse === 'all'
    ? courses
    : courses.filter(c => c.id === selectedCourse)

  // When a course is selected, sort staff by worst status first
  const sortedStaff = selectedCourse === 'all'
    ? staff
    : [...staff].sort((a, b) => {
        const order: Record<Status, number> = { Overdue: 0, DueSoon: 1, NotCertified: 2, NoExpiry: 3, Valid: 4 }
        const aStatus = a.courseStatuses.find(s => s.courseId === selectedCourse)?.status ?? 'NotCertified'
        const bStatus = b.courseStatuses.find(s => s.courseId === selectedCourse)?.status ?? 'NotCertified'
        return order[aStatus] - order[bStatus]
      })

  // Search narrows each axis independently — a term matching a course name filters columns,
  // a term matching a person's name filters rows, and both can apply at once. A term that
  // matches neither axis keeps the current view rather than hiding everything.
  const searchLower = search.trim().toLowerCase()
  const courseMatches = searchLower ? visibleCourses.filter(c => c.name.toLowerCase().includes(searchLower)) : visibleCourses
  const staffMatches = searchLower ? sortedStaff.filter(s => s.fullName.toLowerCase().includes(searchLower)) : sortedStaff
  const noSearchMatches = !!searchLower && courseMatches.length === 0 && staffMatches.length === 0

  const gridCourses = !searchLower ? visibleCourses : (courseMatches.length > 0 ? courseMatches : visibleCourses)
  const gridStaff = !searchLower ? sortedStaff : (staffMatches.length > 0 ? staffMatches : sortedStaff)

  // ── Certificate view/download (single cell) ─────────────────────────────────
  const handleView = async (recordId: string, courseName: string) => {
    const res = await portalApi.streamTeamCertificate(recordId)
    const blob: Blob = res.data
    const blobUrl = URL.createObjectURL(blob)
    setViewerUrl(prev => { if (prev) URL.revokeObjectURL(prev); return blobUrl })
    setViewerContentType(blob.type || 'application/octet-stream')
    setViewerTitle(courseName)
  }

  const handleDownload = async (recordId: string) => {
    const res = await portalApi.streamTeamCertificate(recordId)
    const blobUrl = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = 'certificate'
    a.click()
    URL.revokeObjectURL(blobUrl)
  }

  // ── Select mode ──────────────────────────────────────────────────────────────
  function certIdsForRow(member: typeof sortedStaff[number]): string[] {
    return gridCourses
      .map(c => member.courseStatuses.find(s => s.courseId === c.id))
      .filter((cs): cs is PortalStaffCourseStatusDto => !!cs?.hasCertificate && !!cs.trainingRecordId)
      .map(cs => cs.trainingRecordId!)
  }

  function certIdsForColumn(courseId: string): string[] {
    return gridStaff
      .map(m => m.courseStatuses.find(s => s.courseId === courseId))
      .filter((cs): cs is PortalStaffCourseStatusDto => !!cs?.hasCertificate && !!cs.trainingRecordId)
      .map(cs => cs.trainingRecordId!)
  }

  function allVisibleCertIds(): string[] {
    return gridStaff.flatMap(certIdsForRow)
  }

  function toggleGroup(ids: string[]) {
    if (ids.length === 0) return
    setSelected(prev => {
      const allSelected = ids.every(id => prev.has(id))
      const next = new Set(prev)
      ids.forEach(id => allSelected ? next.delete(id) : next.add(id))
      return next
    })
  }

  function toggleOne(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function toggleSelectMode() {
    setSelectMode(m => !m)
    setSelected(new Set())
  }

  async function handleDownloadSelected() {
    if (selected.size === 0) return
    setDownloading(true)
    try {
      const res = await portalApi.downloadTeamCertificatesZip([...selected])
      const blobUrl = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = 'certificates.zip'
      a.click()
      URL.revokeObjectURL(blobUrl)
      setSelected(new Set())
    } catch {
      alert('Failed to download certificates.')
    } finally {
      setDownloading(false)
    }
  }

  const allVisibleIds = allVisibleCertIds()
  const allVisibleSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selected.has(id))

  return (
    <div className="space-y-6">
      {viewerUrl && (
        <CertificateViewerModal
          blobUrl={viewerUrl}
          contentType={viewerContentType}
          title={viewerTitle}
          onClose={() => {
            URL.revokeObjectURL(viewerUrl)
            setViewerUrl(null)
          }}
        />
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Compliance</h1>
        <p className="text-sm text-gray-500 mt-1">See which team members are certified for each course</p>
        <p className="text-xs text-gray-400 mt-1 sm:hidden">Scroll sideways to see all courses →</p>
      </div>

      {/* Search + select mode controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-56 max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search person or course…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-secondary)]"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
              <X size={14} />
            </button>
          )}
        </div>

        <button
          onClick={toggleSelectMode}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
            selectMode
              ? 'bg-[var(--brand-primary)] text-[var(--brand-primary-contrast)] border-[var(--brand-primary)]'
              : 'bg-white text-gray-600 border-gray-300 hover:border-[var(--brand-secondary)] hover:text-[var(--brand-secondary)]'
          }`}
        >
          <ListChecks size={15} />
          {selectMode ? 'Done selecting' : 'Select certificates'}
        </button>

        {selectMode && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">{selected.size} selected</span>
            {selected.size > 0 && (
              <>
                <button onClick={() => setSelected(new Set())} className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer">
                  Clear
                </button>
                <button
                  onClick={handleDownloadSelected}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-[var(--brand-primary)] text-[var(--brand-primary-contrast)] hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer"
                >
                  <Download size={15} />
                  {downloading ? 'Preparing…' : `Download ${selected.size}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {selectMode && (
        <p className="text-xs text-gray-400 -mt-4">
          Click a certificate to select it, a person's name to select their row, or a course name to select the whole column.
        </p>
      )}

      {/* Course filter pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCourse('all')}
          className={`px-3 py-1.5 rounded-[var(--brand-radius-full)] text-sm font-medium border transition-colors ${
            selectedCourse === 'all'
              ? 'bg-[var(--brand-primary)] text-[var(--brand-primary-contrast)] border-[var(--brand-primary)]'
              : 'bg-white text-gray-600 border-gray-300 hover:border-[var(--brand-secondary)] hover:text-[var(--brand-secondary)]'
          }`}
        >
          All courses
        </button>
        {courses.map(c => {
          const pct = c.totalStaff > 0 ? Math.round((c.certifiedCount / c.totalStaff) * 100) : 0
          return (
            <button
              key={c.id}
              onClick={() => setSelectedCourse(c.id)}
              className={`px-3 py-1.5 rounded-[var(--brand-radius-full)] text-sm font-medium border transition-colors flex items-center gap-2 ${
                selectedCourse === c.id
                  ? 'bg-[var(--brand-primary)] text-[var(--brand-primary-contrast)] border-[var(--brand-primary)]'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-[var(--brand-secondary)] hover:text-[var(--brand-secondary)]'
              }`}
            >
              {c.name}
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                selectedCourse === c.id
                  ? 'bg-white/20 text-white'
                  : pct === 100 ? 'bg-green-100 text-green-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
              }`}>
                {c.certifiedCount}/{c.totalStaff}
              </span>
            </button>
          )
        })}
      </div>

      {/* Compliance grid */}
      {noSearchMatches ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-400">
          No people or courses match "{search}".
        </div>
      ) : (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap sticky left-0 bg-gray-50 z-10">
                  {selectMode ? (
                    <button
                      onClick={() => toggleGroup(allVisibleIds)}
                      disabled={allVisibleIds.length === 0}
                      className="inline-flex items-center gap-1.5 cursor-pointer disabled:cursor-default disabled:opacity-40 hover:text-[var(--brand-secondary)]"
                      title="Select all"
                    >
                      {allVisibleSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                      Staff member
                    </button>
                  ) : 'Staff member'}
                </th>
                {gridCourses.map(c => {
                  const colIds = selectMode ? certIdsForColumn(c.id) : []
                  const colSelected = selectMode && colIds.length > 0 && colIds.every(id => selected.has(id))
                  return (
                    <th
                      key={c.id}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {selectMode ? (
                        <button
                          onClick={() => toggleGroup(colIds)}
                          disabled={colIds.length === 0}
                          className="inline-flex items-center gap-1.5 cursor-pointer disabled:cursor-default disabled:opacity-40 hover:text-[var(--brand-secondary)]"
                          title="Select column"
                        >
                          {colSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                          {c.name}
                        </button>
                      ) : c.name}
                    </th>
                  )
                })}
                {!selectMode && <th className="px-4 py-3 w-8" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {gridStaff.map(member => {
                const rowIds = selectMode ? certIdsForRow(member) : []
                const rowSelected = selectMode && rowIds.length > 0 && rowIds.every(id => selected.has(id))

                return (
                <tr
                  key={member.staffMemberId}
                  className={`transition-colors ${selectMode ? '' : 'hover:bg-slate-50 cursor-pointer'}`}
                  onClick={selectMode ? undefined : () => navigate(`/portal/team/${member.staffMemberId}`)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white">
                    {selectMode ? (
                      <button
                        onClick={() => toggleGroup(rowIds)}
                        disabled={rowIds.length === 0}
                        className="inline-flex items-center gap-1.5 cursor-pointer disabled:cursor-default disabled:opacity-40 hover:text-[var(--brand-secondary)]"
                        title="Select row"
                      >
                        {rowSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                        {member.fullName}
                      </button>
                    ) : member.fullName}
                  </td>
                  {gridCourses.map(c => {
                    const cs = member.courseStatuses.find(s => s.courseId === c.id)
                    const status: Status = cs?.status ?? 'NotCertified'
                    const recordId = cs?.hasCertificate ? cs.trainingRecordId : null
                    const isSelected = !!recordId && selected.has(recordId)

                    return (
                      <td
                        key={c.id}
                        className={`group px-4 py-3 whitespace-nowrap ${StatusCell({ status })} ${
                          selectMode && recordId ? 'cursor-pointer' : ''
                        } ${selectMode && !recordId ? 'opacity-35' : ''} ${
                          isSelected ? 'ring-2 ring-inset ring-[var(--brand-primary)]' : ''
                        }`}
                        onClick={selectMode && recordId ? (e) => { e.stopPropagation(); toggleOne(recordId) } : undefined}
                      >
                        <div className="flex items-center gap-2">
                          {selectMode && recordId && (
                            isSelected
                              ? <CheckSquare size={13} className="text-[var(--brand-primary)] shrink-0" />
                              : <Square size={13} className="text-gray-500 shrink-0" />
                          )}
                          <StatusBadge status={status} expiryDate={cs?.expiryDate ?? null} />
                          {!selectMode && recordId && (
                            <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                title="View certificate"
                                onClick={e => { e.stopPropagation(); handleView(recordId, c.name) }}
                                className="p-1 rounded hover:bg-white/70 text-gray-400 hover:text-gray-700 cursor-pointer"
                              >
                                <Eye size={13} />
                              </button>
                              <button
                                title="Download certificate"
                                onClick={e => { e.stopPropagation(); handleDownload(recordId) }}
                                className="p-1 rounded hover:bg-white/70 text-gray-400 hover:text-gray-700 cursor-pointer"
                              >
                                <Download size={13} />
                              </button>
                            </span>
                          )}
                        </div>
                      </td>
                    )
                  })}
                  {!selectMode && (
                    <td className="px-4 py-3">
                      <ChevronRight size={14} className="text-gray-300 ml-auto" />
                    </td>
                  )}
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-green-600" /> Certified / No expiry</span>
        <span className="flex items-center gap-1"><Clock size={12} className="text-amber-600" /> Expiring within 30 days</span>
        <span className="flex items-center gap-1"><AlertTriangle size={12} className="text-red-600" /> Overdue</span>
        <span className="flex items-center gap-1"><MinusCircle size={12} className="text-gray-400" /> Not certified</span>
      </div>
    </div>
  )
}
